/**
 * CheckoutScreen — full-size payment surface, replaces the modal PaymentDialog.
 *
 *  ┌──────────────────────────────────────┬─────────────────────────────────┐
 *  │  Tendered amount (display)            │  Customer + invoice summary     │
 *  │  Method chips                         │  ─────────────────────────────  │
 *  │  10-key keypad                        │  Split-payment ledger           │
 *  │  Quick-cash chips                     │  Reference field                │
 *  │  ─────────────────────────────────    │  Balance / Change indicator     │
 *  │  Add tender                           │  Complete sale                  │
 *  └──────────────────────────────────────┴─────────────────────────────────┘
 *
 * Keyboard:
 *   0–9 . Backspace      build amount
 *   Enter                add tender (or Complete if balance is zero)
 *   Esc                  back to cart
 *   F1/F2/F3/F4          Cash / UPI / Card / Credit
 *   F9                   Complete sale
 *   Ctrl+Backspace       clear amount
 */

import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Trash2, Delete, UserPlus, Pencil, MessageCircle } from 'lucide-react';
import { useCartStore, type PaymentMethod } from '@/store/useCartStore';
import { billTimestamp } from '@/billing/billDate';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useCreateInvoice } from '@/billing/useCreateInvoice';
import { useInvoicePrint } from '@/printing';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { applyTender, exceedsCreditLimit, toRupees } from '@/billing/billingEngine';
import { getActiveDatabase } from '@/db';
import { Customer } from '@/db/models/Customers';
import { CustomerPickerDialog } from './CustomerPickerDialog';

const METHODS: { value: PaymentMethod; label: string; hint: string }[] = [
  { value: 'CASH', label: 'Cash', hint: 'F1' },
  { value: 'UPI', label: 'UPI', hint: 'F2' },
  { value: 'CARD', label: 'Card', hint: 'F3' },
  { value: 'CREDIT', label: 'Credit', hint: 'F4' },
];

interface Props {
  onBack: () => void;
  onComplete: (result: {
    invoiceId: string;
    invoiceNumber: string;
    total: number;
    customerPhone: string | null;
  }) => void;
}

export function CheckoutScreen({ onBack, onComplete }: Props) {
  const totals = useCartStore((s) => s.totals);
  const items = useCartStore((s) => s.items);
  const itemCount = useCartStore((s) => s.itemCount);
  const customer = useCartStore((s) => s.customer);
  const quickBill = useCartStore((s) => s.quickBill);
  const billDate = useCartStore((s) => s.billDate);
  const payments = useCartStore((s) => s.payments);
  const paidAmountPaisa = useCartStore((s) => s.paidAmountPaisa);
  const creditAmountPaisa = useCartStore((s) => s.creditAmountPaisa);
  const addPayment = useCartStore((s) => s.addPayment);
  const removePayment = useCartStore((s) => s.removePayment);

  const { createInvoice, isCreating } = useCreateInvoice();
  const { printDefault } = useInvoicePrint();

  /**
   * Everything the bill is accounted for by: money received, plus debt the
   * customer has explicitly taken on. A CREDIT tender settles the bill without
   * settling the account, so it counts here and never towards paidAmountPaisa.
   */
  const settledPaisa = paidAmountPaisa + creditAmountPaisa;
  /** Outstanding balance, never negative. */
  const balanceRupees = useMemo(
    () => toRupees(Math.max(0, totals.finalTotalPaisa - settledPaisa)),
    [totals.finalTotalPaisa, settledPaisa],
  );
  /** Cash overage (when tendered > total) — informational, kept in sync via payments. */
  const overpaidRupees = useMemo(
    () => toRupees(Math.max(0, settledPaisa - totals.finalTotalPaisa)),
    [totals.finalTotalPaisa, settledPaisa],
  );
  const isPaid = balanceRupees <= 0.001;

  const [method, setMethod] = useState<PaymentMethod>('CASH');
  /** Controls the attach/change-customer picker dialog. */
  const [customerOpen, setCustomerOpen] = useState(false);
  /**
   * Tendered amount as a free-form string the keypad mutates directly.
   * Keeping it as a string (not a number) lets us preserve trailing dots
   * during entry (e.g. user typing "100.") without flickering.
   */
  const [amountInput, setAmountInput] = useState<string>('');
  const [reference, setReference] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));
  /**
   * Cash handed back to the customer across this checkout.
   *
   * Tracked rather than derived, because once the drawer is open and the notes
   * are in the customer's hand the change is a fact, not a function of what is
   * currently in the payment list. Reset when the list is emptied.
   */
  const [changeDueRupees, setChangeDueRupees] = useState(0);
  /**
   * Set to the completion payload once the invoice is persisted.
   * Prevents a second createInvoice() call if the user accidentally
   * re-triggers F9/Enter after a print failure.
   */
  const [completedPayload, setCompletedPayload] = useState<{
    invoiceId: string; invoiceNumber: string; total: number; customerPhone: string | null;
  } | null>(null);

  // Pre-fill amount with the outstanding balance whenever it changes.
  // Using a ref so manual edits aren't clobbered mid-typing.
  const lastBalanceRef = useRef<number>(NaN);
  useEffect(() => {
    if (balanceRupees !== lastBalanceRef.current) {
      lastBalanceRef.current = balanceRupees;
      setAmountInput(balanceRupees > 0 ? balanceRupees.toFixed(2) : '');
    }
  }, [balanceRupees]);

  // Clearing the payment list restarts the tender, so the change given against
  // the old one no longer applies.
  useEffect(() => {
    if (payments.length === 0) setChangeDueRupees(0);
  }, [payments.length]);

  const refInputRef = useRef<HTMLInputElement>(null);

  // ── Quick-cash denominations ──────────────────────────
  const quickCashAmounts = useMemo(() => {
    const due = balanceRupees > 0 ? balanceRupees : totals.totalAmount;
    if (due <= 0) return [] as number[];
    const denoms = [
      due,
      Math.ceil(due / 100) * 100,
      Math.ceil(due / 500) * 500,
      Math.ceil(due / 1000) * 1000,
    ];
    return Array.from(new Set(denoms.filter((d) => d >= due))).slice(0, 4);
  }, [balanceRupees, totals.totalAmount]);

  // ─────────────────────────────────────────────────────────────────
  // Mutators
  // ─────────────────────────────────────────────────────────────────

  /** Append a digit/dot to the amount, with sane single-decimal-point rules. */
  function pushKey(k: string) {
    setAmountInput((prev) => {
      // First digit replaces the prefilled balance — feels natural after
      // the user starts typing instead of having to clear first.
      const base = lastBalanceRef.current === parseFloat(prev) ? '' : prev;
      if (k === '.') {
        if (base.includes('.')) return base;
        return base === '' ? '0.' : `${base}.`;
      }
      // Block more than 2 decimal places
      const dot = base.indexOf('.');
      if (dot !== -1 && base.length - dot > 2) return base;
      // Strip leading zero unless followed by a dot
      if (base === '0' && k !== '.') return k;
      return `${base}${k}`;
    });
  }

  function backspace() {
    setAmountInput((prev) => {
      // Same "first edit replaces prefill" rule
      if (lastBalanceRef.current === parseFloat(prev)) return '';
      return prev.slice(0, -1);
    });
  }

  function clearAmount() {
    setAmountInput('');
    lastBalanceRef.current = NaN;
  }

  /** Record a tender, keeping only what the shop keeps. See applyTender(). */
  function recordTender(m: PaymentMethod, amount: number, ref?: string) {
    const { applied, change } = applyTender(m, amount, balanceRupees);
    if (change > 0) setChangeDueRupees((c) => c + change);
    // A cash tender against a settled bill is all change and no payment.
    if (applied > 0) addPayment({ method: m, amount: applied, reference: ref });
  }

  function handleAdd() {
    const amt = parseFloat(amountInput);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError('Enter a valid amount');
      return;
    }
    setError(null);
    recordTender(method, amt, reference.trim() || undefined);
    setReference('');
    // amount auto-resets via the balance effect
  }

  function handleQuickCash(amount: number) {
    setMethod('CASH');
    setError(null);
    recordTender('CASH', amount);
    setReference('');
  }

  async function handleComplete() {
    // Guard: invoice already created (e.g. print failed on first attempt).
    if (completedPayload) {
      onComplete(completedPayload);
      return;
    }
    if (!isPaid && !(payments.length === 0 && method === 'CREDIT')) {
      setError('Outstanding balance — add a tender or use Credit');
      return;
    }
    // Debt needs a debtor. A CREDIT tender, or an unpaid remainder, has to land
    // on somebody's khata — otherwise the sale books revenue against nobody and
    // the money is never chased. Mobile has always required this; desktop and
    // web did not.
    const owedPaisa = creditAmountPaisa + Math.max(0, totals.finalTotalPaisa - settledPaisa);
    if (owedPaisa > 0 && !customer) {
      setError('Attach a customer — a credit sale has to go on someone’s account.');
      return;
    }
    // A credit limit the shopkeeper set is a decision, not a suggestion. Mobile
    // has always checked this; desktop and web never did, so the same customer
    // could run up any balance at a counter rather than on a phone. Read live
    // rather than from the cart's snapshot — the balance moves with every sale
    // and every khata payment, including ones synced from another device.
    if (owedPaisa > 0 && customer) {
      try {
        const row = await getActiveDatabase().get<Customer>('customers').find(customer.id);
        const owedRupees = toRupees(owedPaisa);
        if (exceedsCreditLimit(row.balance, row.creditLimit, owedRupees)) {
          setError(
            `This sale would take ${customer.name || 'this customer'} past their credit limit. ` +
            `Balance ${formatCurrency(row.balance)} + ${formatCurrency(owedRupees)} ` +
            `exceeds ${formatCurrency(row.creditLimit)}.`,
          );
          return;
        }
      } catch {
        // The customer row could not be read. Do not block the sale over a
        // lookup failure — an unreadable limit is not an exceeded one — but the
        // cashier still gets a customer-attached credit sale, which is recorded.
        console.warn('[Checkout] Could not verify credit limit for', customer.id);
      }
    }
    setError(null);
    // If a credit-only sale with no tenders, push a zero-amount? No — the
    // mobile flow accepts a sale with `payments=[]` and lets the engine
    // mark it UNPAID. So we just call createInvoice() as-is.
    const result = await createInvoice();
    if (!result.success) {
      setError(result.error || 'Failed to create invoice');
      return;
    }
    const payload = {
      invoiceId: result.invoiceId!,
      invoiceNumber: result.invoiceNumber!,
      total: totals.totalAmount,
      customerPhone: customer?.phone ?? null,
    };
    if (useSettingsStore.getState().getBoolean('THERMAL_AUTO_PRINT_ON_SALE')) {
      const printResult = await printDefault(result.invoiceId!);
      if (!printResult.ok) {
        // Lock the screen so no second invoice is created, but keep the
        // user here so they can read the error before pressing Continue.
        setCompletedPayload(payload);
        setError(`Sale saved. Auto-print failed: ${printResult.message ?? 'check printer settings'}`);
        return;
      }
    }
    onComplete(payload);
  }

  // ─────────────────────────────────────────────────────────────────
  // Keyboard
  // ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // While the customer picker is open, let the dialog own the keyboard
      // (its own Esc/Enter/arrows) — don't run checkout shortcuts underneath it.
      if (customerOpen) return;

      // Don't hijack typing in the reference input
      const target = e.target as HTMLElement | null;
      const inField = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');

      if (e.key === 'Escape') {
        e.preventDefault();
        onBack();
        return;
      }
      if (e.key === 'F9') {
        e.preventDefault();
        handleComplete();
        return;
      }
      // Method shortcuts
      if (e.key === 'F1') { e.preventDefault(); setMethod('CASH'); return; }
      if (e.key === 'F2') { e.preventDefault(); setMethod('UPI'); return; }
      if (e.key === 'F3') { e.preventDefault(); setMethod('CARD'); return; }
      if (e.key === 'F4') { e.preventDefault(); setMethod('CREDIT'); return; }

      if (inField) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        if (isPaid) handleComplete();
        else handleAdd();
        return;
      }
      if (e.key === 'Backspace') {
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) clearAmount();
        else backspace();
        return;
      }
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        pushKey(e.key);
        return;
      }
      if (e.key === '.') {
        e.preventDefault();
        pushKey('.');
        return;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaid, method, amountInput, reference, customerOpen]);

  // ─────────────────────────────────────────────────────────────────

  const tenderedNum = parseFloat(amountInput) || 0;
  const willOverpay = method === 'CASH' && tenderedNum > balanceRupees && balanceRupees > 0;

  return (
    <div className="grid h-full grid-cols-1 content-start gap-3 overflow-auto lg:content-normal lg:grid-cols-[1fr_440px] lg:gap-6 lg:overflow-visible">
      {/* ════════════════ LEFT: amount + keypad ════════════════ */}
      <section className="flex flex-col rounded-lg border border-stroke-hairline/10 bg-bg-surface lg:min-h-0">
        {/* Header bar */}
        <div className="flex items-center justify-between border-b border-stroke-hairline/10 px-5 py-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 rounded-md px-2 py-1.5 text-body text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary transition-colors"
            title="Back to cart (Esc)"
          >
            <ArrowLeft size={14} /> Back to cart
          </button>
          {quickBill && (
            <span
              className="rounded-full bg-accent/10 px-3 py-1 text-label text-accent-ink"
              data-testid="checkout-bill-date"
            >
              Quick Bill · dated {new Date(billTimestamp(billDate)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          )}
          <div className="hidden text-label uppercase text-fg-tertiary sm:block">
            Esc · Back  ·  F9 · Complete
          </div>
        </div>

        {/* Tendered display */}
        <div className="flex flex-col items-center gap-1 border-b border-stroke-hairline/10 py-4 sm:py-6">
          <div className="text-label uppercase text-fg-tertiary">
            Tendered ({METHODS.find((m) => m.value === method)?.label})
          </div>
          <div className={cn(
            'text-[40px] font-semibold leading-none tracking-tight tabular-nums sm:text-[64px]',
            tenderedNum > 0 ? 'text-fg-primary' : 'text-fg-tertiary',
          )}>
            {amountInput === '' ? formatCurrency(0) : `₹${amountInput}`}
          </div>
          {willOverpay && (
            <div className="text-body text-success-ink tabular-nums">
              Change: {formatCurrency(tenderedNum - balanceRupees)}
            </div>
          )}
        </div>

        {/* Method chips */}
        <div className="flex flex-wrap gap-2 px-5 pt-4">
          {METHODS.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => setMethod(m.value)}
              className={cn(
                'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-body transition-colors duration-120 ease-apple',
                method === m.value
                  ? 'btn-brand border border-white/10'
                  : 'bg-bg-subtle text-fg-secondary hover:bg-stroke-hairline/10 hover:text-fg-primary',
              )}
            >
              {m.label}
              <span className={cn(
                'text-label uppercase',
                method === m.value ? 'text-white/70' : 'text-fg-tertiary',
              )}>{m.hint}</span>
            </button>
          ))}
        </div>

        {/* Keypad */}
        <div className="flex-1 px-5 py-4">
          <div className="grid grid-cols-3 gap-2">
            {(['7','8','9','4','5','6','1','2','3','.','0'] as const).map((k) => (
              <KeypadButton key={k} onClick={() => pushKey(k)}>{k}</KeypadButton>
            ))}
            <KeypadButton onClick={backspace} aria-label="Backspace">
              <Delete size={20} />
            </KeypadButton>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Button variant="secondary" onClick={clearAmount}>Clear</Button>
            <Button onClick={handleAdd} disabled={!(parseFloat(amountInput) > 0)}>
              Add tender
            </Button>
          </div>
        </div>

        {/* Quick cash */}
        {quickCashAmounts.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-t border-stroke-hairline/10 px-5 py-3">
            <span className="text-label uppercase text-fg-tertiary">Quick cash</span>
            {quickCashAmounts.map((amt) => (
              <button
                key={amt}
                type="button"
                onClick={() => handleQuickCash(amt)}
                className="rounded-md border border-stroke-hairline/15 bg-bg-canvas px-3 py-1.5 text-body text-fg-secondary tabular-nums hover:border-accent/40 hover:text-fg-primary transition-colors"
              >
                {formatCurrency(amt)}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* ════════════════ RIGHT: summary + ledger ════════════════ */}
      <aside className="flex flex-col rounded-lg border border-stroke-hairline/10 bg-bg-surface lg:min-h-0">
        {/* Customer + totals */}
        <div className="border-b border-stroke-hairline/10 px-5 py-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-label uppercase text-fg-tertiary">Bill to</div>
              <div className="text-body text-fg-primary truncate">
                {customer?.name?.trim() || 'Walk-in customer'}
              </div>
              {customer?.phone && (
                <div className="text-label text-fg-tertiary tabular-nums">{customer.phone}</div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setCustomerOpen(true)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-stroke-hairline/15 px-2.5 py-1.5 text-label uppercase text-fg-secondary hover:border-accent/40 hover:text-fg-primary transition-colors"
            >
              {customer ? <><Pencil size={12} /> Change</> : <><UserPlus size={12} /> Add customer</>}
            </button>
          </div>

          {/* Nudge: a phone number is needed to send the invoice via WhatsApp/SMS. */}
          {!customer?.phone && (
            <button
              type="button"
              onClick={() => setCustomerOpen(true)}
              className="mt-2 flex w-full items-center gap-1.5 rounded-md bg-accent/5 px-2.5 py-1.5 text-left text-label text-accent-ink hover:bg-accent/10 transition-colors"
            >
              <MessageCircle size={12} className="shrink-0" />
              {customer
                ? 'Add a phone number to send the invoice on WhatsApp'
                : 'Add customer to send the invoice on WhatsApp'}
            </button>
          )}

          <div className="mt-4 space-y-1.5">
            <Row label={`${itemCount} ${itemCount === 1 ? 'item' : 'items'}`} value={formatCurrency(totals.subtotal)} muted />
            {totals.globalDiscount > 0 && (
              <Row label="Discount" value={`− ${formatCurrency(totals.globalDiscount)}`} muted />
            )}
            {totals.totalTax > 0 && (
              <Row label="Tax" value={formatCurrency(totals.totalTax)} muted />
            )}
            {totals.roundOff !== 0 && (
              <Row label="Round off" value={formatCurrency(totals.roundOff)} muted />
            )}
            <div className="flex items-baseline justify-between border-t border-stroke-hairline/10 pt-2">
              <span className="text-heading text-fg-primary">Total</span>
              <span className="text-total text-fg-primary tabular-nums">
                {formatCurrency(totals.totalAmount)}
              </span>
            </div>
          </div>
        </div>

        {/* Reference field */}
        <div className="border-b border-stroke-hairline/10 px-5 py-3">
          <label className="text-label uppercase text-fg-tertiary">
            {method === 'CASH' ? 'Notes' : method === 'CREDIT' ? 'Credit note' : 'Reference / txn id'}
          </label>
          <Input
            ref={refInputRef}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder={method === 'CASH' ? 'optional' : 'optional'}
            className="mt-1"
          />
        </div>

        {/* Ledger */}
        <div className="flex-1 overflow-auto">
          {payments.length === 0 ? (
            <div className="flex h-full items-center justify-center px-6 text-center text-body text-fg-tertiary">
              {items.length === 0
                ? 'Cart is empty.'
                : 'Tap Add tender to record a payment.'}
            </div>
          ) : (
            <ul className="divide-y divide-stroke-hairline/10">
              {payments.map((p, i) => (
                <li key={i} className="flex items-center justify-between gap-2 px-5 py-2.5">
                  <div className="min-w-0">
                    <div className="text-body text-fg-primary">{p.method}</div>
                    {p.reference && (
                      <div className="truncate text-label text-fg-tertiary">{p.reference}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-body text-fg-primary tabular-nums">
                      {formatCurrency(p.amount)}
                    </span>
                    <button
                      type="button"
                      onClick={() => removePayment(i)}
                      className="rounded p-1 text-fg-tertiary hover:bg-danger/10 hover:text-danger-ink transition-colors"
                      aria-label="Remove payment"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Balance + complete */}
        <div className="border-t border-stroke-hairline/10 px-5 py-4 space-y-3">
          {payments.length > 0 && (
            <Row label="Tendered" value={formatCurrency(toRupees(paidAmountPaisa))} muted />
          )}
          {changeDueRupees > 0 || overpaidRupees > 0 ? (
            // Change due is the one number a cashier computes on every cash
            // sale — make it the loudest thing on the screen. It now reports the
            // cash actually handed back; the payment rows carry only what the
            // shop kept, so this no longer doubles as an overpayment reading.
            <div className="rounded-lg bg-success/10 px-4 py-3 text-center">
              <div className="text-label uppercase tracking-wide text-success-ink">Change due</div>
              <div className="mt-0.5 text-display text-success-ink tabular-nums">
                {formatCurrency(changeDueRupees + overpaidRupees)}
              </div>
              <div className="text-label text-fg-tertiary">Return to customer</div>
            </div>
          ) : (
            <Row
              label={isPaid ? 'Settled' : 'Balance'}
              value={formatCurrency(balanceRupees)}
              accent={isPaid ? 'success' : 'warning'}
            />
          )}

          {/* Soft guard against accidental ₹0 / negative sales — warns, never blocks. */}
          {items.length > 0 && totals.totalAmount <= 0 && (
            <div className="rounded-md bg-warning/10 px-3 py-2 text-body text-warning-ink">
              This sale totals {formatCurrency(totals.totalAmount)} — complete only if that’s intended.
            </div>
          )}

          <Button
            block
            size="lg"
            onClick={handleComplete}
            disabled={isCreating || (items.length === 0 && !completedPayload) || (!isPaid && method !== 'CREDIT' && payments.length === 0 && !completedPayload)}
          >
            {isCreating
              ? 'Saving…'
              : completedPayload
                ? 'Continue  ·  F9'
                : isPaid
                  ? 'Complete sale  ·  F9'
                  : payments.length === 0 && method === 'CREDIT'
                    ? 'Save as credit  ·  F9'
                    : 'Save with balance  ·  F9'}
          </Button>
        </div>
      </aside>

      {/* Attach / change customer — reuses the cart's picker (writes to the
          same cart store, so the Bill-to panel updates reactively). */}
      <Dialog open={customerOpen} onOpenChange={setCustomerOpen}>
        <DialogContent title="Attach customer" className="w-[520px]">
          <CustomerPickerDialog onClose={() => setCustomerOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
//  Pieces
// ═════════════════════════════════════════════════════════════════════

function KeypadButton({
  children,
  onClick,
  ...rest
}: {
  children: React.ReactNode;
  onClick: () => void;
  'aria-label'?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...rest}
      className="flex h-12 items-center justify-center rounded-md border border-stroke-hairline/15 bg-bg-canvas text-[22px] font-medium sm:h-14 text-fg-primary tabular-nums hover:border-accent/40 hover:bg-bg-subtle active:bg-stroke-hairline/15 transition-colors duration-120 ease-apple"
    >
      {children}
    </button>
  );
}

function Row({
  label,
  value,
  muted,
  accent,
}: {
  label: string;
  value: string;
  muted?: boolean;
  accent?: 'success' | 'warning';
}) {
  const valueClass =
    accent === 'success'
      ? 'text-success-ink'
      : accent === 'warning'
        ? 'text-warning-ink'
        : muted
          ? 'text-fg-secondary'
          : 'text-fg-primary';
  return (
    <div className="flex items-center justify-between text-body">
      <span className={muted ? 'text-fg-tertiary' : 'text-fg-secondary'}>{label}</span>
      <span className={cn('tabular-nums', valueClass)}>{value}</span>
    </div>
  );
}
